import { Button, Form, Input, Modal, Radio, Select } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  IdentifierCategory,
  IdentifierType,
  StrongIdentifier,
} from '../../../Utils/Constants/identifierType';
import {
  validAadhar,
  validAccNo,
  validEmail,
  validMobile,
  validPan,
} from '../../../Utils/Constants/identifierValidator';
import OTPInputComponent from '../../Common/Components/OTPModal';
import '../index.css';

interface IAddUserIdentificationProps {
  visible: boolean;
  handleCancel: () => void;
  identifierEntered: (otp: any) => void;
  isAddIdentifierVerification: boolean;
  onOTPSubmit: (otp: string, identifierNumber: any) => void;
  label: string;
  multipleIdentifiers: boolean;
  requiredCategory?: string[];
  requiredType?: string[];
  defaultAddIdentifier?: any;
}

const UserIdentification: React.FC<IAddUserIdentificationProps> = ({
  visible,
  handleCancel,
  identifierEntered,
  isAddIdentifierVerification,
  onOTPSubmit,
  children,
  label,
  multipleIdentifiers,
  requiredCategory,
  requiredType,
  defaultAddIdentifier,
}) => {
  const [t] = useTranslation();
  const [identifierCategory, setIdentifierCategory] = useState(
    IdentifierCategory.STRONG
  );
  const [identifierObject, setIdentifierObject] = useState({
    categoryType: '',
    type: '',
    value: '',
  });
  const user = useSelector((state: any) => state.user.user);
  const loading: boolean = useSelector(
    (state: any) => state.loader.searchLoader
  );
  const [selectedIdentifierType, setSelectedIdentifierType] =
    useState('MOBILE');
  const onOTPValueSubmit = (otp: string) => {
    onOTPSubmit(otp, identifierObject);
  };

  const setDefault = () => {
    if (defaultAddIdentifier?.category) {
      setIdentifierCategory(defaultAddIdentifier.category);
      setSelectedIdentifierType(defaultAddIdentifier.type);
      form.setFieldsValue({
        categoryType: defaultAddIdentifier?.category,
        type: defaultAddIdentifier?.type,
      });
    } else {
      form.resetFields();
      setIdentifierCategory(IdentifierCategory.STRONG);
    }
  };
  useEffect(() => {
    setDefault();
  }, [defaultAddIdentifier]);
  const onCancel = () => {
    handleCancel();
    setIdentifierObject({ categoryType: '', type: '', value: '' });
    setDefault();
  };
  const onIdentifierEntered = () => {
    identifierEntered(identifierObject);
  };
  const onIdentifierTypeChange = (value: any) => {
    setSelectedIdentifierType(value);
    form.setFieldValue('value', '');
  };
  const customValidator = (_u: any, value: string) => {
    if (value) {
      switch (selectedIdentifierType) {
        case StrongIdentifier.MOBILE: {
          if (!validMobile.test(value)) {
            return Promise.reject(new Error(t('Invalid Mobile Number')));
          }
          break;
        }
        case IdentifierType.AADHAR: {
          if (!validAadhar.test(value)) {
            return Promise.reject(new Error(t('Invalid Aadhar')));
          }
          break;
        }
        case IdentifierType.PAN: {
          if (!validPan.test(value)) {
            return Promise.reject(new Error(t('Invalid Pan')));
          }
          break;
        }
        case IdentifierType.EMAIL: {
          if (!validEmail.test(value)) {
            return Promise.reject(new Error(t('Invalid Email')));
          }
          break;
        }
        case IdentifierType.ACCNO: {
          if (!validAccNo.test(value)) {
            return Promise.reject(new Error(t('Invalid Account Number')));
          }
          break;
        }
      }
    }
    return Promise.resolve();
  };

  const [form] = useForm();

  return (
    <>
      {isAddIdentifierVerification ? (
        <>
          <OTPInputComponent
            title={multipleIdentifiers ? t('Add Identifier') : t('Reset PIN')}
            isOpen={visible}
            onOtpSubmit={onOTPValueSubmit}
            onCancel={onCancel}
            okText={t('Confirm')}
            onResend={onIdentifierEntered}
            mobileNumber={
              multipleIdentifiers &&
              identifierObject.categoryType === IdentifierCategory.STRONG
                ? identifierObject.value
                : user.mobileNumber
            }
          >
            {children}
          </OTPInputComponent>
        </>
      ) : (
        <>
          <Modal
            title={label}
            open={visible}
            onCancel={onCancel}
            okButtonProps={{ hidden: true }}
            cancelButtonProps={{ hidden: true }}
            footer={null}
          >
            <Form
              form={form}
              // tslint:disable-next-line: jsx-no-lambda
              onFinish={(values) => {
                identifierEntered(values);
                setIdentifierObject(values);
              }}
              // tslint:disable-next-line: jsx-no-lambda
              onFieldsChange={() => {
                if (
                  form?.getFieldValue('categoryType') !== identifierCategory
                ) {
                  setIdentifierCategory(form?.getFieldValue('categoryType'));

                  form?.setFieldsValue({ type: '', value: '' });
                }
              }}
            >
              {multipleIdentifiers && (
                <>
                  <Form.Item
                    name='categoryType'
                    label='Category of Identifier'
                    rules={[
                      {
                        required: true,
                        message: '* Required field',
                      },
                    ]}
                    initialValue={IdentifierCategory.STRONG}
                  >
                    <Radio.Group>
                      {requiredCategory?.map((i) => (
                        <Radio value={i}>{i}</Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    name='type'
                    label='Type'
                    rules={[
                      {
                        required: true,
                        message: 'Please select type of Identifier!',
                      },
                    ]}
                  >
                    <Select
                      onChange={onIdentifierTypeChange}
                      placeholder='Please select type of Identifier'
                    >
                      {identifierCategory === IdentifierCategory.STRONG
                        ? Object.keys(StrongIdentifier)
                            .filter((i) => requiredType?.includes(i))
                            .map((identifier1, index) => (
                              <Select.Option key={index} value={identifier1}>
                                {identifier1}
                              </Select.Option>
                            ))
                        : Object.keys(IdentifierType)
                            .filter((i) => requiredType?.includes(i))
                            .map((identifier1, index) => (
                              <Select.Option key={index} value={identifier1}>
                                {identifier1}
                              </Select.Option>
                            ))}
                    </Select>
                  </Form.Item>
                </>
              )}
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: '* Required field',
                  },
                  {
                    validator: customValidator,
                  },
                ]}
                name='value'
                className='identifierFormItem formItemMarginBotton'
              >
                <Input
                  className='identifierInput'
                  placeholder='Enter mobile number'
                />
              </Form.Item>
              <div className='formButtonGroup'>
                <Form.Item className='formItemMarginBotton'>
                  <Button onClick={onCancel} type='default' htmlType='button'>
                    {t('Cancel')}
                  </Button>
                </Form.Item>
                <Form.Item className='primaryButton customizeButton formItemMarginBotton confirmButton'>
                  <Button htmlType='submit' type='primary' loading={loading}>
                    {t('Confirm')}
                  </Button>
                </Form.Item>
              </div>
            </Form>
          </Modal>
        </>
      )}
    </>
  );
};
export default UserIdentification;
